<script lang="ts">
export default {
  name: 'ExternalPolicyBinderReviewPage',
};
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowLeft, Loader, FileWarning, CheckCircle2, Trash2 } from '@lucide/vue';
import ExtractionReviewShell from '@/components/document/ExtractionReviewShell.vue';
import ExternalPolicyActionPanel from '@/components/v2/external_policy_upload/ExternalPolicyActionPanel.vue';
import BinderReviewNav from '@/components/v2/binder_review/BinderReviewNav.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { useBinderReviewSequence, binderReviewRouteQuery } from '@/composables/useBinderReviewSequence';
import { externalPolicyBinderService } from '@/services/v2/ExternalPolicyBinderService';
import { aiService } from '@/services/AIService';
import { referenceService } from '@/services/api';
import type { ExternalPolicyBinderSubmission } from '@/types/ExternalPolicyBinder';
import type { AIPromptResult } from '@/types/ai';
import type { BinderReviewSource } from '@/types/BinderReview';

// Reviewing a scanned binder: the same extraction surface the AI results page uses, with
// the create-a-policy action panel instead of the override one. The submission's own
// states (still scanning, failed, already resolved) wrap it.
const POLL_INTERVAL_MS = 4000;
const MAX_POLL_INTERVAL_MS = 30000;

const route = useRoute();
const router = useRouter();

const accountId = computed(() => String(route.params.id));
const submissionId = computed(() => Number(route.params.submissionId));

const submission = ref<ExternalPolicyBinderSubmission | null>(null);
const promptResult = ref<AIPromptResult | null>(null);
const loadError = ref<string | null>(null);
// Values a resolved entity may be corrected to. Fetched here rather than in the shared
// field list, which is also the internal review's and knows nothing of external policies.
const entityOptions = ref<Partial<Record<'carrier' | 'coverage_type' | 'wholesaler', string[]>>>({});
// The group each selectable value rolls up to, so a correction can show what it resolves to
// without a second round trip.
const entityGroups = ref<Record<string, string>>({});
const entityOptionsError = ref(false);
const unsavedCount = ref(0);

// The client's results are one sequence across both binder sources, so Next can step
// from this submission straight onto an internal binder's review.
// The filters the list was showing when this submission was opened, so Back/Next stays
// inside the set the reviewer was working through.
const filters = computed(() => ({
  status: route.query.status ? String(route.query.status) : '',
  source: (route.query.source ? String(route.query.source) : '') as BinderReviewSource | '',
  search: route.query.search ? String(route.query.search) : '',
}));

const { currentIndex, prevItem, nextItem, position, routeFor } = useBinderReviewSequence(
  accountId,
  () => ({
    source: 'external' as const,
    submissionId: submissionId.value,
  }),
  filters,
);

let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollDelay = POLL_INTERVAL_MS;
let unmounted = false;

// The API reports `ready` for any completed invocation, including one that produced no
// prompt result -- there is nothing to review in that case.
const extractionFailed = computed(
  () => submission.value?.status === 'failed' || (submission.value?.status === 'ready' && !submission.value.promptResultId),
);

const policiesRoute = computed(() => ({ path: `/account/${accountId.value}/policy-detail` }));

const listRoute = computed(() => ({
  name: 'account-binder-reviews',
  params: { id: accountId.value },
  query: binderReviewRouteQuery(filters.value),
}));

async function refresh() {
  try {
    submission.value = await externalPolicyBinderService.getSubmission(accountId.value, submissionId.value);
    loadError.value = null;
    pollDelay = POLL_INTERVAL_MS;

    if (submission.value.status === 'ready' && submission.value.promptResultId && !promptResult.value) {
      promptResult.value = await aiService.getPromptResultWithReviews(submission.value.promptResultId);
    }
  } catch (err) {
    loadError.value = (err as Error).message || 'Could not load the submission';
    // A blip while the extraction is still running would otherwise strand the page on the
    // error until the user reloads.
    if (!unmounted && (!submission.value || submission.value.status === 'processing')) {
      pollDelay = Math.min(pollDelay * 2, MAX_POLL_INTERVAL_MS);
      pollTimer = setTimeout(refresh, pollDelay);
    }
    return;
  }

  if (!unmounted && submission.value.status === 'processing') {
    pollTimer = setTimeout(refresh, pollDelay);
  }
}

function goToList() {
  router.push(listRoute.value);
}

function go(target: typeof prevItem.value) {
  if (target) {
    router.push(routeFor(target));
  }
}

// A policy was created, so land where it is visible: external policies are hidden behind
// the toggle by default, and the broker would otherwise arrive at a list without it.
function goToCreatedPolicy() {
  router.push({ ...policiesRoute.value, query: { ext: 'true' } });
}

onMounted(async () => {
  refresh();
  try {
    const options = await referenceService.externalPolicyEntities();
    entityOptions.value = {
      carrier: options.issuingCompanies.map((option) => option.name),
      coverage_type: options.coverageTypes,
      wholesaler: options.wholesalers.map((option) => option.name),
    };
    entityGroups.value = Object.fromEntries(
      [...options.issuingCompanies, ...options.wholesalers].map((option) => [option.name, option.groupName]),
    );
    entityOptionsError.value = false;
  } catch {
    // Falling back to free text would let a reviewer type a value the create then rejects,
    // with nothing on screen explaining why. Say the lists are missing instead.
    entityOptions.value = {};
    entityGroups.value = {};
    entityOptionsError.value = true;
  }
});

onUnmounted(() => {
  unmounted = true;
  if (pollTimer) {
    clearTimeout(pollTimer);
  }
});
</script>

<template>
  <div class="flex h-full min-h-0 flex-col gap-3 p-4">
    <div class="flex shrink-0 items-center gap-3">
      <AppButton
        data-testid="back-to-policies"
        @click="goToList">
        <ArrowLeft :size="16" /> Binder Scans
      </AppButton>
      <h1 class="text-xl font-semibold">Review Scanned External Binder</h1>
      <span
        v-if="submission?.documentName"
        class="truncate text-sm text-base-content/60">
        {{ submission.documentName }}
      </span>

      <BinderReviewNav
        v-if="currentIndex >= 0"
        class="ml-auto"
        :has-prev="!!prevItem"
        :has-next="!!nextItem"
        :position="position"
        :unsaved-count="unsavedCount"
        @prev="go(prevItem)"
        @next="go(nextItem)" />
    </div>

    <div
      v-if="loadError"
      class="alert alert-error"
      data-testid="binder-review-error">
      <span>{{ loadError }}</span>
    </div>

    <div
      v-else-if="!submission"
      class="flex items-center gap-2 py-6"
      data-testid="binder-review-loading">
      <Loader
        :size="20"
        class="animate-spin text-secondary" />
      <span>Loading submission…</span>
    </div>

    <template v-else>
      <div
        v-if="submission.status === 'processing'"
        class="flex flex-col items-center gap-3 rounded-lg border border-base-300 p-10"
        data-testid="binder-review-processing">
        <Loader
          :size="32"
          class="animate-spin text-secondary" />
        <p class="font-medium">Scanning binder…</p>
        <p class="text-sm text-base-content/60">This can take a few minutes. You can leave this page and come back.</p>
      </div>

      <div
        v-else-if="extractionFailed"
        class="flex flex-col items-center gap-3 rounded-lg border border-error/40 p-10"
        data-testid="binder-review-failed">
        <FileWarning
          :size="32"
          class="text-error" />
        <p class="font-medium">We couldn't extract this binder</p>
        <p
          v-if="submission.errorMessage"
          class="text-sm text-base-content/60">
          {{ submission.errorMessage }}
        </p>
        <p
          v-else
          class="text-sm text-base-content/60">
          The scan finished without returning any policy details.
        </p>
        <AppButton @click="goToList">Back to binder scans</AppButton>
      </div>

      <div
        v-else-if="submission.status === 'confirmed'"
        class="flex flex-col items-center gap-3 rounded-lg border border-success/40 p-10"
        data-testid="binder-review-confirmed">
        <CheckCircle2
          :size="32"
          class="text-success" />
        <p class="font-medium">This binder has already been confirmed</p>
        <AppButton @click="goToList">Back to binder scans</AppButton>
      </div>

      <div
        v-else-if="submission.status === 'discarded'"
        class="flex flex-col items-center gap-3 rounded-lg border border-base-300 p-10"
        data-testid="binder-review-discarded">
        <Trash2
          :size="32"
          class="text-base-content/60" />
        <p class="font-medium">This submission was discarded</p>
        <p class="text-sm text-base-content/60">{{ submission.documentName }}</p>
        <AppButton @click="goToList">Back to binder scans</AppButton>
      </div>

      <ExtractionReviewShell
        v-else-if="promptResult"
        :result="promptResult"
        :entity-options="entityOptions"
        :entity-groups="entityGroups"
        :entity-options-failed="entityOptionsError"
        @pending-changes="(count) => (unsavedCount = count)">
        <template #action-panel="{ target, entityMatches, entityValues, pendingEditCount }">
          <ExternalPolicyActionPanel
            v-if="target?.kind === 'new_external_policy'"
            :account-id="accountId"
            :target="target"
            :entity-matches="entityMatches"
            :entity-values="entityValues"
            :pending-edit-count="pendingEditCount"
            @created="goToCreatedPolicy"
            @discarded="goToList" />
        </template>
      </ExtractionReviewShell>
    </template>
  </div>
</template>
